import * as React from 'react';
import { AddressType } from '../../types/address';

interface AddressTypeSelectorProps {
  value: AddressType;
  onChange: (type: AddressType) => void;
  disabled?: boolean;
}

interface TypeOption {
  value: AddressType;
  label: string;
  description: string;
  icon: React.ReactNode;
}

const typeOptions: TypeOption[] = [
  {
    value: 'street',
    label: 'Calle / Avenida',
    description: 'Direccion con calle y numero',
    icon: (
      <svg className="w-6 h-6 sm:w-7 sm:h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    )
  },
  {
    value: 'country',
    label: 'Barrio Cerrado',
    description: 'Country, barrio privado o housing',
    icon: (
      <svg className="w-6 h-6 sm:w-7 sm:h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
      </svg>
    )
  }
];

export function AddressTypeSelector({ value, onChange, disabled = false }: AddressTypeSelectorProps) {
  return (
    <fieldset className="mb-6 border-none p-0" disabled={disabled}>
      <legend className="sr-only">Tipo de direccion</legend>
      <p className="text-[15px] font-semibold text-gray-800 mb-4">Tipo de direccion</p>

      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4" role="radiogroup" aria-label="Tipo de direccion">
        {typeOptions.map((option) => {
          const isSelected = value === option.value;

          return (
            <label
              key={option.value}
              className={`relative flex items-start gap-3 p-4 sm:p-5 rounded-xl border-2 cursor-pointer transition-all duration-200 flex-1 ${
                disabled ? 'opacity-50 cursor-not-allowed' : ''
              } ${
                isSelected
                  ? 'border-green-500 bg-green-50 shadow-[0_0_0_3px_rgba(34,197,94,0.2)]'
                  : 'border-gray-300 bg-white hover:border-gray-400 hover:bg-gray-50'
              }`}
            >
              <input
                type="radio"
                name="address-type"
                value={option.value}
                checked={isSelected}
                onChange={() => onChange(option.value)}
                className="sr-only"
                aria-describedby={`${option.value}-description`}
                disabled={disabled}
              />

              <div className={`shrink-0 w-11 h-11 sm:w-[52px] sm:h-[52px] rounded-[10px] flex items-center justify-center ${
                isSelected ? 'bg-green-500 text-white' : 'bg-gray-100 text-gray-500'
              }`}>
                {option.icon}
              </div>

              <div className="flex-1 min-w-0">
                <span className={`block font-semibold text-base sm:text-[17px] ${
                  isSelected ? 'text-green-800' : 'text-gray-800'
                }`}>
                  {option.label}
                </span>
                <span
                  id={`${option.value}-description`}
                  className={`block text-sm mt-0.5 ${
                    isSelected ? 'text-green-600' : 'text-gray-500'
                  }`}
                >
                  {option.description}
                </span>
              </div>

              {isSelected && (
                <div className="absolute top-2 right-2 w-[22px] h-[22px] text-green-500">
                  <svg className="w-full h-full" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
              )}
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}
